import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-523a33fd"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "compute-wizard-friction__recommendation-container" }
const _hoisted_2 = ["src"]
const _hoisted_3 = {
  class: "compute-wizard-friction__input-accordion-item compute-wizard-friction__input-accordion-item--calculator",
  slot: "content"
}
const _hoisted_4 = { class: "compute-wizard-friction__friction-calculator-title-wrapper" }
const _hoisted_5 = { class: "compute-wizard-friction__calculator-button-inner" }
const _hoisted_6 = { class: "compute-wizard-friction__calculator-button-label" }
const _hoisted_7 = { class: "compute-wizard-friction__calculator-button-value" }
const _hoisted_8 = { class: "compute-wizard-friction__friction-calculator-title-wrapper" }
const _hoisted_9 = { class: "compute-wizard-friction__calculator-button-inner" }
const _hoisted_10 = { class: "compute-wizard-friction__calculator-button-label" }
const _hoisted_11 = { class: "compute-wizard-friction__calculator-button-value" }

import { computed, ref, watch } from 'vue';
import { IonButton, IonCol, IonGrid, IonInput, IonItem, IonLabel, IonRow, IonText } from '@ionic/vue';
import { useI18n } from 'vue-i18n';
import { InvalidFormatType } from '@/enums/InvalidFormat';
import { FrictionType } from '@/enums/Friction';
import { ConditionType } from '@/enums/Condition';
import { TranslateElement } from '@/types/TranslateElement';
import { useFrictionStepStore } from '@/store/frictionStep';
import useTranslateEnums from '@/composables/useTranslateEnums';
import { getSpecificFrictionCoefficient } from '@/composables/useCalculationResult';
import useAlertOnEvent from '@/composables/useAlertOnEvent';
import SegmentSelect from '@/components/SegmentSelect.vue';
import ComputeWizardStepContainer from '@/components/ComputeWizard/ComputeWizardStepContainer.vue';
import ComputeWizardInputAccordion from '@/components/ComputeWizard/ComputeWizardInputAccordion.vue';
import Friction from '@/assets/images/step-schemas/friction.png';


export default /*#__PURE__*/_defineComponent({
  __name: 'ComputeWizardFriction',
  setup(__props) {

const { t } = useI18n();
const { translate } = useTranslateEnums();
const alertManager = useAlertOnEvent();
const frictionStepStore = useFrictionStepStore();

const frictionTypeTranslations = computed((): Array<TranslateElement> => {
  return translate(FrictionType, 'friction.types');
});

const frictionTypePopUpLabels = computed(() => {
  return [t('friction.popup_labels.weak'), t('friction.popup_labels.medium'), t('friction.popup_labels.strong')];
});

const minMeasuredTorque = ref(undefined);
const minMeasuredPreload = ref(undefined);

const minMeasuredTorqueInputStep = computed(() => {
  return minMeasuredTorque.value && minMeasuredTorque.value > 100 ? 0.1 : 0.01;
});

const maxMeasuredTorque = ref(undefined);
const maxMeasuredPreload = ref(undefined);

const maxMeasuredTorqueInputStep = computed(() => {
  return maxMeasuredTorque.value && maxMeasuredTorque.value > 100 ? 0.1 : 0.01;
});

const specificCoefficientMax = ref(frictionStepStore.specificCoefficientMax);
const specificCoefficientMin = ref(frictionStepStore.specificCoefficientMin);

const minMeasured = computed(() => {
  if (minMeasuredTorque.value && minMeasuredPreload.value) {
    return getSpecificFrictionCoefficient(parseFloat(minMeasuredTorque.value), parseFloat(minMeasuredPreload.value));
  }

  return undefined;
});

const maxMeasured = computed(() => {
  if (maxMeasuredTorque.value && maxMeasuredPreload.value) {
    return getSpecificFrictionCoefficient(parseFloat(maxMeasuredTorque.value), parseFloat(maxMeasuredPreload.value));
  }

  return undefined;
});

const onFrictionTypeChange = (newValue: FrictionType | undefined) => {
  frictionStepStore.setType(newValue);

  specificCoefficientMin.value = frictionStepStore.specificCoefficientMin;
  specificCoefficientMax.value = frictionStepStore.specificCoefficientMax;
};

const validateValue = async (field_name: string, current_value: number, decimalCount = 0): Promise<boolean> => {
  if (current_value && current_value <= 0) {
    await alertManager.fireConditionAlert({
      field_name,
      current_value,
      conditionType: ConditionType.GreaterThan,
      target: 0,
    });
    return false;
  }

  const valueDecimalCount = current_value.toString().split('.')[1];

  if (valueDecimalCount && valueDecimalCount.length > decimalCount) {
    await alertManager.fireInvalidFormat({
      field_name,
      current_value,
      invalidFormatType: decimalCount === 0 ? InvalidFormatType.Integer : InvalidFormatType.MaxDecimal,
      maxDecimal: decimalCount,
      integer: decimalCount === 0,
    });
    return false;
  }

  return true;
};

const torqueAndPreloadAreValid = async (
  fieldNamePrefix: string,
  torque: number | undefined,
  preload: number | undefined
): Promise<boolean> => {
  let torqueIsValid = true;
  let preloadIsValid = true;

  if (torque !== undefined) {
    torqueIsValid = await validateValue(
      `${fieldNamePrefix} ${t('friction.calculator.measured_torque')}`,
      torque,
      torque > 100 ? 1 : 2
    );
  }

  if (torqueIsValid && preload !== undefined) {
    preloadIsValid = await validateValue(`${fieldNamePrefix} ${t('friction.calculator.measured_preload')}`, preload);
  }

  return torqueIsValid && preloadIsValid;
};

const onMinMeasuredValueSelected = async (): Promise<void> => {
  if (
    !(await torqueAndPreloadAreValid(t('friction.calculator.min'), minMeasuredTorque.value, minMeasuredPreload.value))
  ) {
    return;
  }

  if (minMeasured.value && !Number.isNaN(minMeasured.value) && Number.isFinite(minMeasured.value)) {
    frictionStepStore.setSpecificCoefficientMin(minMeasured.value);
    specificCoefficientMin.value = frictionStepStore.specificCoefficientMin;
  }
};

const onMaxMeasuredValueSelected = async (): Promise<void> => {
  if (
    !(await torqueAndPreloadAreValid(t('friction.calculator.max'), maxMeasuredTorque.value, maxMeasuredPreload.value))
  ) {
    return;
  }

  if (maxMeasured.value && !Number.isNaN(maxMeasured.value) && Number.isFinite(maxMeasured.value)) {
    frictionStepStore.setSpecificCoefficientMax(maxMeasured.value);
    specificCoefficientMax.value = frictionStepStore.specificCoefficientMax;
  }
};

watch(specificCoefficientMin, (currentValue) => {
  frictionStepStore.setSpecificCoefficientMin(currentValue);

  specificCoefficientMin.value = frictionStepStore.specificCoefficientMin;
  specificCoefficientMax.value = frictionStepStore.specificCoefficientMax;
});

watch(specificCoefficientMax, (currentValue) => {
  frictionStepStore.setSpecificCoefficientMax(currentValue);

  specificCoefficientMin.value = frictionStepStore.specificCoefficientMin;
  specificCoefficientMax.value = frictionStepStore.specificCoefficientMax;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ComputeWizardStepContainer, { class: "compute-wizard-friction" }, {
    default: _withCtx(() => [
      _createVNode(SegmentSelect, {
        class: "compute-wizard-friction__type",
        modelValue: _unref(frictionStepStore).type,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(frictionStepStore).type) = $event)),
        "pop-up-labels": frictionTypePopUpLabels.value,
        options: frictionTypeTranslations.value,
        onOnChange: onFrictionTypeChange
      }, null, 8, ["modelValue", "pop-up-labels", "options"]),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(IonText), { class: "compute-wizard-friction__recommendation-title" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('friction.recommendation_title')), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(IonText), { class: "compute-wizard-friction__recommendation-content" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('friction.recommendation_content')), 1)
          ]),
          _: 1
        })
      ]),
      _createElementVNode("img", {
        class: "compute-wizard-friction__image",
        src: _unref(Friction)
      }, null, 8, _hoisted_2),
      _createVNode(ComputeWizardInputAccordion, {
        class: "compute-wizard-friction__input-accordion",
        title: _ctx.$t('friction.specific_settings.accordion_label')
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(IonItem), {
            lines: "full",
            class: "compute-wizard-friction__input-accordion-item",
            slot: "content"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonLabel), { class: "compute-wizard-friction__specific-coefficient-title" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('friction.specific_coefficient_title')), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(IonItem), {
            lines: "full",
            class: "compute-wizard-friction__input-accordion-item",
            slot: "content"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonLabel), null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('friction.coefficient.min_input_label')), 1)
                ]),
                _: 1
              }),
              _createVNode(_unref(IonInput), {
                class: "compute-wizard-friction__coefficient-input",
                type: "number",
                inputMode: "decimal",
                pattern: "[0-9]*",
                step: _unref(frictionStepStore).specificCoefficientStep,
                min: _unref(frictionStepStore).specificCoefficientMinMinimumValue + _unref(frictionStepStore).specificCoefficientStep,
                max: _unref(frictionStepStore).specificCoefficientMinMaximumValue - _unref(frictionStepStore).specificCoefficientStep,
                placeholder: "x",
                modelValue: specificCoefficientMin.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((specificCoefficientMin).value = $event))
              }, null, 8, ["step", "min", "max", "modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(IonItem), {
            lines: "full",
            class: "compute-wizard-friction__input-accordion-item",
            slot: "content"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonLabel), null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('friction.coefficient.max_input_label')), 1)
                ]),
                _: 1
              }),
              _createVNode(_unref(IonInput), {
                class: "compute-wizard-friction__coefficient-input",
                type: "number",
                inputMode: "decimal",
                pattern: "[0-9]*",
                step: _unref(frictionStepStore).specificCoefficientStep,
                min: _unref(frictionStepStore).specificCoefficientMaxMinimumValue + _unref(frictionStepStore).specificCoefficientStep,
                max: _unref(frictionStepStore).specificCoefficientMaxMaximumValue - _unref(frictionStepStore).specificCoefficientStep,
                placeholder: "x",
                modelValue: specificCoefficientMax.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((specificCoefficientMax).value = $event))
              }, null, 8, ["step", "min", "max", "modelValue"])
            ]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_unref(IonLabel), { class: "compute-wizard-friction__friction-coefficient-title" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('friction.friction_coefficient_title')), 1)
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(IonLabel), { class: "compute-wizard-friction__friction-calculator-subtitle" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('friction.calculator.min')), 1)
                ]),
                _: 1
              })
            ]),
            _createVNode(_unref(IonGrid), { class: "compute-wizard-friction__calculator-grid" }, {
              default: _withCtx(() => [
                _createVNode(_unref(IonRow), { class: "compute-wizard-friction__calculator-row" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(IonCol), { class: "compute-wizard-friction__calculator-col" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(IonItem), { class: "compute-wizard-friction__calculator-item" }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(IonLabel), {
                              for: "min-measured-torque",
                              class: "compute-wizard-friction__calculator-label",
                              position: "stacked"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_ctx.$t('friction.calculator.measured_torque')), 1)
                              ]),
                              _: 1
                            }),
                            _createVNode(_unref(IonInput), {
                              id: "min-measured-torque",
                              class: "compute-wizard-friction__calculator-input",
                              type: "number",
                              inputMode: "decimal",
                              pattern: "[0-9]*",
                              step: minMeasuredTorqueInputStep.value,
                              min: "0.01",
                              modelValue: minMeasuredTorque.value,
                              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((minMeasuredTorque).value = $event)),
                              placeholder: "x"
                            }, null, 8, ["step", "modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(IonCol), { class: "compute-wizard-friction__calculator-col" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(IonItem), { class: "compute-wizard-friction__calculator-item" }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(IonLabel), {
                              for: "min-measured-preload",
                              class: "compute-wizard-friction__calculator-label",
                              position: "stacked"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_ctx.$t('friction.calculator.measured_preload')), 1)
                              ]),
                              _: 1
                            }),
                            _createVNode(_unref(IonInput), {
                              id: "min-measured-preload",
                              class: "compute-wizard-friction__calculator-input",
                              type: "number",
                              inputMode: "decimal",
                              pattern: "[0-9]*",
                              step: "1",
                              min: "1",
                              modelValue: minMeasuredPreload.value,
                              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((minMeasuredPreload).value = $event)),
                              placeholder: "x"
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(IonCol), { class: "compute-wizard-friction__calculator-col compute-wizard-friction__calculator-col--button" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(IonButton), {
                          class: "compute-wizard-friction__calculator-button",
                          color: "light",
                          onClick: onMinMeasuredValueSelected
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_5, [
                              _createElementVNode("span", _hoisted_6, _toDisplayString(_ctx.$t('friction.coefficient.min_input_label')), 1),
                              _createElementVNode("span", _hoisted_7, _toDisplayString(minMeasured.value), 1)
                            ])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_unref(IonLabel), { class: "compute-wizard-friction__friction-calculator-subtitle" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('friction.calculator.max')), 1)
                ]),
                _: 1
              })
            ]),
            _createVNode(_unref(IonGrid), { class: "compute-wizard-friction__calculator-grid" }, {
              default: _withCtx(() => [
                _createVNode(_unref(IonRow), { class: "compute-wizard-friction__calculator-row" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(IonCol), { class: "compute-wizard-friction__calculator-col" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(IonItem), { class: "compute-wizard-friction__calculator-item" }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(IonLabel), {
                              for: "max-measured-torque",
                              class: "compute-wizard-friction__calculator-label",
                              position: "stacked"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_ctx.$t('friction.calculator.measured_torque')), 1)
                              ]),
                              _: 1
                            }),
                            _createVNode(_unref(IonInput), {
                              id: "max-measured-torque",
                              class: "compute-wizard-friction__calculator-input",
                              type: "number",
                              inputMode: "decimal",
                              pattern: "[0-9]*",
                              step: maxMeasuredTorqueInputStep.value,
                              min: "0.01",
                              modelValue: maxMeasuredTorque.value,
                              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((maxMeasuredTorque).value = $event)),
                              placeholder: "x"
                            }, null, 8, ["step", "modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(IonCol), { class: "compute-wizard-friction__calculator-col" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(IonItem), { class: "compute-wizard-friction__calculator-item" }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(IonLabel), {
                              for: "max-measured-preload",
                              class: "compute-wizard-friction__calculator-label",
                              position: "stacked"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_ctx.$t('friction.calculator.measured_preload')), 1)
                              ]),
                              _: 1
                            }),
                            _createVNode(_unref(IonInput), {
                              id: "max-measured-preload",
                              class: "compute-wizard-friction__calculator-input",
                              type: "number",
                              inputMode: "decimal",
                              pattern: "[0-9]*",
                              step: "1",
                              min: "1",
                              modelValue: maxMeasuredPreload.value,
                              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((maxMeasuredPreload).value = $event)),
                              placeholder: "x"
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(IonCol), { class: "compute-wizard-friction__calculator-col compute-wizard-friction__calculator-col--button" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(IonButton), {
                          class: "compute-wizard-friction__calculator-button",
                          color: "light",
                          onClick: onMaxMeasuredValueSelected
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_9, [
                              _createElementVNode("span", _hoisted_10, _toDisplayString(_ctx.$t('friction.coefficient.max_input_label')), 1),
                              _createElementVNode("span", _hoisted_11, _toDisplayString(maxMeasured.value), 1)
                            ])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ]),
        _: 1
      }, 8, ["title"])
    ]),
    _: 1
  }))
}
}

})